import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Container,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Grid,
  IconButton,
  Drawer,
  TextField,
  FormControlLabel,
  Switch,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Button,
  Checkbox,
  Radio,
  RadioGroup,
  Alert,
  FormLabel,
  Rating,
  Slider,
  InputAdornment,
} from "@mui/material";
import CategoryIcon from "@mui/icons-material/Category";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import OutlinedFlagIcon from "@mui/icons-material/OutlinedFlag";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AddIcon from "@mui/icons-material/Add";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { v4 as uuidv4 } from "uuid";
import { LoadingButton } from "@mui/lab";
import MyQuill from "../quill/MyQuill";
import ParagraphDescription from "./ParagraphDescription";
import Labelinput from "./Labelinput";
import SelectDropDown from "./SelectDropdown";
import MyDatePicker from "./datetime/MyDatePicker";
import MyTimePicker from "./datetime/MyTimePicker";
import MyDateRange from "./datetime/MyDateRange";
import { useNavigate, useParams } from "react-router-dom";
import {
  X as TwitterIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import axios from "axios";
import MuPb from "../widgets/MuPb";
import { API_ENDPOINT, APP_ID } from "../utils/constants";
import useVerifyToken from "../hooks/useVerifyToken";
import {
  getAuthHeader,
  getDbHeader,
  getErrorResponse,
  toastError,
} from "../utils/util";
import ProductCategoriesChecker from "./ProductCategoriesChecker";
import CountryStateCity from "./widgets/CountryStateCity";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { toast } from "react-toastify";
import useGetFormEdit from "../hooks/useGetFormEdit";
import SubmissionConfirmation from "./SubmissionConfirmation";

const getDefaultProperties = (type) => {
  // Return default properties for each field type
  // You can customize these defaults as needed
  switch (type) {
    case "TextField":
      return {
        label: "Text Field",
        placeholder: "Enter text",
        types: INPUT_TYPES,
        type: "text",
      };
    case "PlainText":
      return {
        label: "Plain Text",
        variant: "body1",
        value: "",
        options: [
          "h1",
          "h2",
          "h3",
          "h4",
          "h5",
          "h6",
          "subtitle1",
          "subtitle2",
          "body1",
          "body2",
          "caption",
          "button",
          "overline",
          "inherit",
        ],
      };
    case "ParagraphDescription":
      return {
        label: "Paragraph/Description",
        text: "Paragraph description here...",
        grid: "12",
      };
    case "Checkbox":
      return { label: "Checkbox", grid: "12" };
    case "LabelInput":
      return { label: "Label", grid: "12" };
    case "RadioGroup":
      return {
        label: "Options",
        options: ["Option 1", "Option 2"],
        grid: "12",
      };
    case "Select":
      return { label: "Select", options: ["Option 1", "Option 2"], grid: "12" };

    case "DatePicker":
      return { label: "Date Picker", required: false };
    case "DateRange":
      return { label: "Date Range", required: false };
    case "TimePicker":
      return { label: "Time Picker", required: false };
    case "DocPDF":
      return {
        label: "Upload PDF",
        type: "DocPDF",
        link: "",
      };
    case "FileUpload":
      return { label: "Upload File", type: "image", grid: "12" };
    case "Rating":
      return {
        label: "Rating",
        default: 1.5,
        precision: 0.5,
        max: 5,
        grid: "12",
      };
    case "ProductCategories":
      return {
        label: "Select Product Categories",
        desc: "Product categories will appear here",
        grid: "12",
      };
    case "Switch":
      return { label: "Toggle Switch", grid: "12" };
    case "Slider":
      return { label: "Slider", min: 0, max: 100, grid: "12" };
    case "Divider":
      return { label: "Divider" };
    default:
      return { grid: "12" };
  }
};
export const getIcon = (type, props = {}) => {
  switch (type.toLowerCase()) {
    case "x":
      return <TwitterIcon {...props} />;
    case "fb":
      return <FacebookIcon {...props} />;
    case "ln":
      return <LinkedInIcon {...props} />;
    case "in":
      return <InstagramIcon {...props} />;
    case "phone":
      return <PhoneIcon {...props} />;
    case "email":
      return <EmailIcon {...props} />;
    default:
      return null;
  }
};

export const INPUT_TYPES = [
  "text", // Basic single-line text input
  "number", // Input for numerical values (with optional step, min, max attributes)
  "password", // Masked text input (e.g., for passwords)
  "email", // Input for email addresses
  "search", // Search field with a cancel button (in some browsers)
  "tel", // Input for telephone numbers
  "url", // Input for URLs
  "date", // Input for date values (calendar picker in some browsers)
  "datetime-local", // Input for date and time values without a time zone
  "month", // Input for month and year
  "week", // Input for week number and year
  "time", // Input for time values
  "color", // Input for color values (color picker in some browsers)
];
export const AVAILABLE_FIELDS = [
  {
    id: "TextField",
    type: "TextField",
    label: "Text Field",
    icon: <TextFieldsIcon />,
  },
  {
    id: "PlainText",
    type: "PlainText",
    label: "Plain Text",
    icon: <FormatColorTextIcon />,
  },
  {
    id: "ParagraphDescription",
    type: "ParagraphDescription",
    label: "Paragraph/Description",
    icon: <DescriptionIcon />,
  },
  {
    id: "Checkbox",
    type: "Checkbox",
    label: "Checkbox",
    icon: <CheckBoxIcon />,
  },
  // {
  //   id: "LabelInput",
  //   type: "LabelInput",
  //   label: "Label Input",
  //   icon: <LabelOutlinedIcon />,
  // },
  {
    id: "RadioGroup",
    type: "RadioGroup",
    label: "Radio Group",
    icon: <RadioButtonCheckedIcon />,
  },
  {
    id: "Select",
    type: "Select",
    label: "Select",
    icon: <TextFieldsIcon />,
  },

  // {
  //   id: "DocPDF",
  //   type: "DocPDF",
  //   label: "Upload PDF",
  //   icon: <PictureAsPdfIcon />,
  // },
  {
    id: "DatePicker",
    type: "DatePicker",
    label: "Date Picker",
    icon: <CalendarTodayIcon />,
  },
  {
    id: "TimePicker",
    type: "TimePicker",
    label: "Time Picker",
    icon: <AccessTimeIcon />,
  },
  {
    id: "DateRange",
    type: "DateRange",
    label: "Date Range",
    icon: <EventRepeatIcon />,
  },
  {
    id: "ProductCategories",
    type: "ProductCategories",
    label: "Product Categories",
    icon: <CategoryIcon />,
  },
  // {
  //   id: "FileUpload",
  //   type: "FileUpload",
  //   label: "File Upload",
  //   icon: <AttachFileIcon />,
  // },
  // {
  //   id: "Rating",
  //   type: "Rating",
  //   label: "Rating",
  //   icon: <StarIcon />,
  // },
  {
    id: "Switch",
    type: "Switch",
    label: "Switch",
    icon: <ToggleOnIcon />,
  },
  // {
  //   id: "Slider",
  //   type: "Slider",
  //   label: "Slider",
  //   icon: <LinearScaleIcon />,
  // },
  {
    id: "CountryStateCity",
    type: "CountryStateCity",
    label: "Country/State/City",
    icon: <OutlinedFlagIcon />,
  },

  {
    id: "Divider",
    type: "Divider",
    label: "Divider",
    icon: <HorizontalRuleIcon />,
  },
];

const RegistrationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    data: formItems,
    isLoading: isFormLoading,
    isError: isFormError,
    error: formError,
    refetch,
  } = useGetFormEdit(id, true);

  const [availableFields, setAvailableFields] = useState(AVAILABLE_FIELDS);
  const [formFields, setFormFields] = useState([]);
  const [terms, setTerms] = useState();
  const [prevHeader, setPrevHeader] = useState();
  const [prevFooter, setPrevFooter] = useState();
  const [savingDraft, setSavingDraft] = useState(false);
  const [cloningForm, setCloningForm] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [file, setFile] = useState(null);

  const [activeStep, setActiveStep] = useState(0);
  const steps = [
    "Create Form",
    "Terms & Conditions",
    "Submission Confirmation",
    "Choose Template",
  ];
  useEffect(() => {
    if (formItems?.form) {
      setFormFields(formItems.form);
      setTerms(formItems.terms);
      setPrevHeader(formItems.prevHeader);
      setPrevFooter(formItems.prevFooter);
    }

    if (formItems?.eventName) {
      document.title = formItems.eventName;
    }
  }, [formItems]);
  const handleSaveForm = useCallback(async () => {
    try {
      setSavingDraft(true);

      const { data } = await axios.post(
        `${API_ENDPOINT}event/submit-draft`,
        {
          form: formFields,
          terms,
          prevHeader,
          prevFooter,
        },
        getAuthHeader(id)
      );

      toast.success(data.message);
    } catch (error) {
      toastError(error);
    } finally {
      setSavingDraft(false);
    }
  }, [formFields, terms, prevHeader, prevFooter, id]);

  const handleCloneFromVisitor = useCallback(async () => {
    try {
      setCloningForm(true);
      const response = await axios.post(
        `${API_ENDPOINT}event/clone-from-visitor`,
        {},
        getAuthHeader(id)
      );
      toast.success(response.data.message);
      refetch();
    } catch (error) {
      toastError(error);
    } finally {
      setCloningForm(false);
    }
  }, []);
  if (isFormLoading) return <MuPb />;
  if (isFormError)
    return <Alert severity="error">{getErrorResponse(formError)}</Alert>;

  const handleFieldEdit = (fieldId) => {
    //const field = formFields.find((f) => f.id === fieldId);
    setSelectedFieldId(fieldId);
    setIsDrawerOpen(true);
  };

  const handleFieldDelete = (fieldId) => {
    setFormFields((prev) => prev.filter((f) => f.id !== fieldId));
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (destination.droppableId !== "formFields") return;

    if (source.droppableId === "availableFields") {
      const sourceField = availableFields[source.index];
      const newField = {
        ...sourceField,
        id: uuidv4(),
        properties: getDefaultProperties(sourceField.type),
      };
      const newFormFields = Array.from(formFields);
      newFormFields.splice(destination.index, 0, newField);
      setFormFields(newFormFields);
    } else {
      const reorderedFields = Array.from(formFields);
      const [movedField] = reorderedFields.splice(source.index, 1);
      reorderedFields.splice(destination.index, 0, movedField);
      setFormFields(reorderedFields);
    }
  };

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);
  const handleSubmit = () => {};

  const renderFormElement = (field) => {
    const { type, properties } = field;
    switch (type) {
      case "TextField":
        return (
          <TextField
            type={properties.type}
            label={properties.label}
            placeholder={properties.placeholder}
            required={properties.required}
            multiline={properties.multiline}
            rows={properties.rows}
            InputProps={
              properties.iconed && properties.iconType
                ? {
                    startAdornment: (
                      <InputAdornment position="start">
                        {getIcon(properties.iconType, {
                          fontSize: "small",
                          color: "action",
                        })}
                      </InputAdornment>
                    ),
                  }
                : null
            }
            fullWidth
            variant="outlined"
            size="small"
            disabled
          />
        );
      case "PlainText":
        return (
          <Typography variant={properties.variant}>
            {properties.label}
          </Typography>
        );
      case "Checkbox":
        return (
          <FormControlLabel
            control={<Checkbox />}
            disabled
            label={properties.label}
            required={properties.required}
          />
        );
      case "Divider":
        return <Divider />;
      case "ParagraphDescription":
        return <ParagraphDescription properties={field.properties} />;

      case "LabelInput":
        return <Labelinput label={properties.label} />;
      case "RadioGroup":
        return (
          <FormControl component="fieldset">
            <Typography variant="subtitle1">{properties.label}</Typography>
            <RadioGroup row={properties.row}>
              {properties.options.map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={option}
                  control={<Radio disabled />}
                  label={option}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );
      case "Select":
        return <SelectDropDown properties={properties} />;
      case "ProductCategories":
        return <Typography variant="h6">{properties.desc}</Typography>;
      case "DatePicker":
        return (
          <MyDatePicker
            min={properties.minDate}
            max={properties.maxDate}
            label={properties.label}
          />
        );
      case "CountryStateCity":
        return (
          <CountryStateCity
            onChange={({ country, state, city }) => {
              console.log("Selected Country:", country);
              console.log("Selected State:", state);
              console.log("Selected City:", city);
            }}
            gridContainerProps={{ spacing: 2 }}
            countryGridItemProps={{ xs: 12, md: 4 }}
            stateGridItemProps={{ xs: 12, md: 4 }}
            cityGridItemProps={{ xs: 12, md: 4 }}
            initialCountry={{ name: "India", code: "IN" }} // example
            countryRequired
            stateRequired
          />
        );
      case "TimePicker":
        return <MyTimePicker label={properties.label} />;
      case "DateRange":
        return <MyDateRange label={properties.label} />;
      case "DocPDF":
        return (
          <>
            {properties.link !== "" ? (
              <a href={properties.link} target="_blank">
                <Button
                  variant="outlined"
                  startIcon={<PictureAsPdfIcon />}
                  size="small"
                >
                  {properties.label}
                </Button>
              </a>
            ) : (
              <Stack direction={"row"} gap={2}>
                <PictureAsPdfIcon />
                <Typography variant="body1">{properties.label}</Typography>
              </Stack>
            )}
          </>
        );

      case "FileUpload":
        return (
          <Button
            disabled
            variant="outlined"
            startIcon={
              properties.type === "pdf" ? (
                <PictureAsPdfIcon />
              ) : (
                <ImageOutlinedIcon />
              )
            }
          >
            Upload {properties.type.toUpperCase()}
          </Button>
        );
      case "Rating":
        return (
          <Rating
            value={properties.default}
            defaultValue={properties.default}
            precision={properties.precision}
          />
        );
      case "Slider":
        return (
          <Slider
            min={properties.min || 0}
            max={properties.max || 100}
            value={properties.value}
            step={properties.step || 0.5}
            marks={properties.marks || false}
          />
        );
      case "Switch":
        return (
          <FormControlLabel
            control={
              <Switch
                checked={properties.active}
                defaultChecked={properties.active}
              />
            }
            label={properties.labelText || "Active"}
          />
        );
      default:
        return <Typography variant="body2">{field.type}</Typography>;
    }
  };

  const updateFieldProperties = (fieldId, newProperties) => {
    setFormFields((prevFields) =>
      prevFields.map((field) =>
        field.id === fieldId
          ? { ...field, properties: { ...field.properties, ...newProperties } }
          : field
      )
    );
  };

  const renderEditingPanel = () => {
    const field = formFields.find((f) => f.id === selectedFieldId);
    if (!field) return null;

    const { type, properties } = field;

    const handlValueChange = (propName, newV) => {
      updateFieldProperties(field.id, { [propName]: newV });
    };
    const handlePropertyChange = (propName) => (event) => {
      const value =
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value;
      updateFieldProperties(field.id, { [propName]: value });
    };
    const handleOptionsChange = (index) => (event) => {
      const newOptions = [...properties.options];
      newOptions[index] = event.target.value;
      updateFieldProperties(field.id, { options: newOptions });
    };
    const addOption = () => {
      updateFieldProperties(field.id, { options: [...properties.options, ""] });
    };

    const removeOption = (index) => {
      const newOptions = properties.options.filter((_, i) => i !== index);
      updateFieldProperties(field.id, { options: newOptions });
    };
    const IsRequiredWidget = ({ label, id }) => {
      return (
        <FormControlLabel
          sx={{ ml: 0 }}
          control={
            <Switch
              size="small"
              checked={properties[id]}
              onChange={handlePropertyChange(id)}
            />
          }
          label={label}
        />
      );
    };
    return (
      <Box p={2}>
        <TextField
          size="small"
          label="Label"
          value={properties.label}
          onChange={handlePropertyChange("label")}
          fullWidth
          margin="normal"
        />

        <Box ml={1}>
          <IsRequiredWidget label={"Required?"} id={"required"} />
        </Box>
        {type === "ProductCategories" && (
          <Box>
            <Typography gutterBottom variant="h6">
              Product Categories
            </Typography>
            <ProductCategoriesChecker id={id} />
          </Box>
        )}

        {type === "ParagraphDescription" && (
          <Box mt={1}>
            <MyQuill
              value={properties.text}
              onChange={(newV) => {
                handlValueChange("text", newV);
              }}
            />
          </Box>
        )}
        {type === "FileUpload" && (
          <FormControl>
            <FormLabel>File type</FormLabel>
            <RadioGroup
              value={properties.type}
              onChange={(event) => {
                handlValueChange("type", event.target.value);
              }}
              row
            >
              <FormControlLabel
                value="image"
                control={<Radio />}
                label="Image"
              />
              <FormControlLabel value="pdf" control={<Radio />} label="PDF" />
            </RadioGroup>
          </FormControl>
        )}

        {type === "TextField" && (
          <>
            <Stack my={2} direction={"row"} gap={1} ml={1}>
              <IsRequiredWidget label={"Multiline?"} id={"multiline"} />
              {properties.multiline && (
                <TextField
                  variant="outlined"
                  size="small"
                  type="number"
                  value={properties.rows}
                  label="Number of Rows"
                  onChange={handlePropertyChange("rows")}
                />
              )}
            </Stack>
            <Divider />
            <Stack my={2} direction={"row"} gap={1} ml={1}>
              <IsRequiredWidget label={"Iconed?"} id={"iconed"} />
              {properties.iconed && (
                <FormControl sx={{ my: 1 }} size="small" fullWidth>
                  <InputLabel>Select icon</InputLabel>
                  <Select
                    size="small"
                    value={properties.iconType}
                    label="Select icon type"
                    onChange={handlePropertyChange("iconType")}
                  >
                    {["x", "fb", "ln", "in", "phone", "email"].map((item) => (
                      <MenuItem value={item}>{item}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>

            <FormControl sx={{ my: 1 }} size="small" fullWidth>
              <InputLabel>Select input type</InputLabel>
              <Select
                size="small"
                value={properties.type}
                label="Select input type"
                onChange={handlePropertyChange("type")}
              >
                {properties.types.map((item) => (
                  <MenuItem value={item}>{item}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
        {type === "PlainText" && (
          <Box>
            <FormControl sx={{ my: 1 }} size="small" fullWidth>
              <InputLabel>Select variant</InputLabel>
              <Select
                size="small"
                value={properties.variant}
                label="Select variant"
                onChange={handlePropertyChange("variant")}
              >
                {properties.options.map((item) => (
                  <MenuItem value={item}>{item}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              sx={{ mt: 1 }}
              fullWidth
              variant="outlined"
              size="small"
              type="text"
              value={properties.value}
              label={properties.label}
              onChange={handlePropertyChange("value")}
            />
          </Box>
        )}

        {(type === "RadioGroup" || type === "Select") && (
          <>
            <Typography variant="body1">Options</Typography>
            {properties.options.map((option, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  marginBottom: "8px",
                  alignItems: "center",
                }}
              >
                <TextField
                  size="small"
                  value={option}
                  onChange={handleOptionsChange(index)}
                  fullWidth
                />
                <IconButton
                  size="small"
                  onClick={() => removeOption(index)}
                  color="secondary"
                  aria-label="remove option"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
            <Box display={"flex"} justifyContent={"end"}>
              <Button
                startIcon={<AddIcon />}
                size="small"
                variant="outlined"
                onClick={addOption}
                color="primary"
              >
                Add
              </Button>
            </Box>

            {type === "RadioGroup" && (
              <IsRequiredWidget label={"Is horizontal?"} id={"row"} />
            )}
            {type === "Select" && (
              <Box border={"1px solid #ddd"} my={1} p={1} borderRadius={1}>
                <IsRequiredWidget
                  label={"Is auto select?"}
                  id={"isAutoSelect"}
                />
                <Alert severity="info">
                  Setting this option will enable search and select
                </Alert>

                {properties.isAutoSelect && (
                  <>
                    <IsRequiredWidget
                      label={"Is multi select?"}
                      id={"isMultiSelect"}
                    />
                    <Alert severity="info">
                      Setting this option will enable search and select multiple
                      option
                    </Alert>
                  </>
                )}
              </Box>
            )}
          </>
        )}

        {type === "DatePicker" && (
          <Stack my={2} direction={"row"} gap={2}>
            <MyDatePicker
              label="Min Date"
              value={properties.minDate}
              onChange={(newV) => {
                handlValueChange("minDate", newV);
              }}
            />
            <MyDatePicker
              label="Max Date"
              value={properties.maxDate}
              onChange={(newV) => {
                handlValueChange("maxDate", newV);
              }}
            />
          </Stack>
        )}
        {type === "Rating" && (
          <Box ml={1} mr={1}>
            <Typography variant="body1">Choose the default value:</Typography>
            <Slider
              onChange={(event) => {
                handlValueChange("default", event.target.value);
              }}
              value={properties.default}
              valueLabelDisplay="auto"
              marks
              min={0}
              max={5}
              step={0.5}
            />
          </Box>
        )}

        {type === "Switch" && (
          <Box my={1}>
            <TextField
              fullWidth
              sx={{ mb: 1 }}
              variant="outlined"
              size="small"
              value={properties.labelText}
              label="Switch Label"
              onChange={handlePropertyChange("labelText")}
            />
            <IsRequiredWidget
              id={"active"}
              label={"Is default state active?"}
            />
          </Box>
        )}
        {type === "Slider" && (
          <Box my={1}>
            <TextField
              fullWidth
              sx={{ mb: 1 }}
              variant="outlined"
              size="small"
              value={properties.labelText}
              label="Switch Label"
              onChange={handlePropertyChange("labelText")}
            />
            <IsRequiredWidget
              id={"active"}
              label={"Is default state active?"}
            />
          </Box>
        )}

        <TextField
          sx={{ mt: 2 }}
          size="small"
          fullWidth
          label="Grid Columns (1-12)"
          name="grid"
          type="number"
          value={properties.grid || 12}
          onChange={handlePropertyChange("grid")}
          inputProps={{ min: 1, max: 12 }}
        />
        <Box display={"flex"} justifyContent={"center"}>
          <LoadingButton
            onClick={() => setIsDrawerOpen(false)}
            sx={{ mt: 2 }}
            variant="outlined"
            size="small"
          >
            OK
          </LoadingButton>
        </Box>
      </Box>
    );
  };

  const getStepContent = (stepIndex) => {
    switch (stepIndex) {
      case 0:
        return (
          <>
            <DragDropContext onDragEnd={onDragEnd}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Droppable droppableId="availableFields" isDropDisabled>
                    {(provided) => (
                      <Box ref={provided.innerRef} {...provided.droppableProps}>
                        <Typography variant="h6">Available Fields</Typography>
                        {availableFields.map((field, index) => (
                          <Draggable
                            key={field.id}
                            draggableId={field.id}
                            index={index}
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  padding: "8px",
                                  margin: "4px",
                                  backgroundColor: "#f0f0f0",
                                  borderRadius: "4px",
                                  cursor: "grab",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {field.icon}
                                <Typography
                                  variant="body1"
                                  style={{ marginLeft: "8px" }}
                                >
                                  {field.label}
                                </Typography>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </Grid>
                <Grid item xs={12} md={9}>
                  <Typography variant="h6">
                    Editing {formItems?.formName} Form: {formItems?.eventName}
                  </Typography>
                  <Box p={1} m={1} border={"1px solid #ccc"}>
                    <Droppable droppableId="formFields">
                      {(provided) => (
                        <Grid
                          container
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {formFields.length === 0 && (
                            <Typography
                              m={2}
                              variant="body1"
                              color="textSecondary"
                            >
                              Drag form fields here to build your form
                            </Typography>
                          )}

                          {formFields.map((field, index) => (
                            <Draggable
                              key={field.id}
                              draggableId={field.id}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <Grid
                                  item
                                  xs={
                                    field?.properties?.grid
                                      ? parseInt(field.properties.grid, 10)
                                      : 12
                                  }
                                >
                                  <Box
                                    m={1}
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    style={provided.draggableProps.style}
                                  >
                                    <div
                                      {...provided.dragHandleProps}
                                      style={{
                                        padding: "8px",
                                        marginBottom: "8px",
                                        backgroundColor: snapshot.isDragging
                                          ? "#e0f7fa"
                                          : "#fff",
                                        border: "1px solid #ccc",
                                        borderRadius: "4px",
                                        position: "relative",
                                        display: "flex",
                                        alignItems: "center",
                                        cursor: "grab",
                                      }}
                                    >
                                      <Box flex={1}>
                                        {renderFormElement(field)}
                                      </Box>

                                      <div
                                        style={{
                                          marginLeft: "auto",
                                          opacity: 0.7,
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleFieldEdit(field.id)
                                          }
                                          aria-label="edit"
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleFieldDelete(field.id)
                                          }
                                          aria-label="delete"
                                        >
                                          <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </div>
                                    </div>
                                  </Box>
                                </Grid>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </Grid>
                      )}
                    </Droppable>
                  </Box>
                </Grid>
              </Grid>
            </DragDropContext>
            <Drawer
              anchor="right"
              open={isDrawerOpen}
              onClose={() => setIsDrawerOpen(false)}
              sx={{ "& .MuiDrawer-paper": { width: 550 } }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 1,
                  px: 2,
                  borderBottom: "1px solid #ddd",
                }}
              >
                <Typography variant="h6">Edit Selected field</Typography>
                <IconButton onClick={() => setIsDrawerOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
              {renderEditingPanel()}
            </Drawer>
          </>
        );
      case 1:
        return (
          <>
            <Typography variant="h6" gutterBottom>
              Terms & Conditions
            </Typography>
            <Box mt={1}>
              <MyQuill value={terms} onChange={setTerms} />
            </Box>
          </>
        );
      case 2:
        return (
          <SubmissionConfirmation
            prevHeader={prevHeader}
            setPrevHeader={setPrevHeader}
            prevFooter={prevFooter}
            setPrevFooter={setPrevFooter}
            initialBadgeBg={formItems?.badgeBg}
            appId={id}
          />
        );
      case 3:
        return (
          <div>
            <Typography variant="h6" textAlign={"center"}>
              Choose Template
            </Typography>
            <Button
              onClick={() => {
                navigate(`/reg-template/${id}`);
              }}
              variant="outlined"
              startIcon={<SettingsOutlinedIcon />}
            >
              Click here to choose your template
            </Button>
          </div>
        );
      default:
        return "Unknown step";
    }
  };
  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      alert("Please select a file first.");
      return;
    }

    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await axios.post(
        `${API_ENDPOINT}event/upload-badge-bg`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            dbauth: APP_ID,
          },
        }
      );
      console.log("Upload success:", response.data);
      alert("File uploaded successfully!");
    } catch (error) {
      console.error("Upload error:", error);
      alert("File upload failed.");
    }
  };
  // return <Alert severity="error">For editing date expired!</Alert>;

  return (
    <Container>
      <>
        <Typography textAlign="center" variant="h5" mb={2} mt={2}>
          Create a {formItems?.formName} Form for{" "}
          <strong>{formItems?.eventName}</strong>
        </Typography>

        <Stack
          mb={1}
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Button
            sx={{ py: 0.5 }}
            onClick={() => {
              navigate(`/reg-template/${id}`);
            }}
            size="small"
            variant="outlined"
            startIcon={<SettingsOutlinedIcon fontSize="small" />}
          >
            Customize template here
          </Button>

          <Button
            sx={{ py: 0.5 }}
            onClick={() => {
              navigate(`/reg-email-template-editor/${id}`);
            }}
            size="small"
            variant="outlined"
            startIcon={<EditIcon fontSize="small" />}
          >
            Email Template Editor
          </Button>

          {formItems?.formName === "Trade Delegates Registration" && (
            <LoadingButton
              sx={{ py: 0.5 }}
              loading={cloningForm}
              onClick={handleCloneFromVisitor}
              size="small"
              variant="outlined"
            >
              Clone from visitor
            </LoadingButton>
          )}

          {/* Hides the spacer on mobile so buttons don't have a massive gap */}
          <Box flex={1} display={{ xs: "none", sm: "block" }} />

          <LoadingButton
            sx={{ py: 0.5 }}
            size="small"
            loading={savingDraft}
            variant="outlined"
            onClick={handleSaveForm}
            startIcon={<SaveOutlinedIcon fontSize="small" />}
          >
            Save Draft
          </LoadingButton>
        </Stack>
      </>
      <Stepper activeStep={activeStep}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box mt={4}>{getStepContent(activeStep)}</Box>
      <Box display={"flex"} justifyContent={"end"} mt={3}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          style={{ marginRight: "10px" }}
        >
          Back
        </Button>
        {activeStep < steps.length - 1 && (
          <Button variant="contained" color="primary" onClick={handleNext}>
            Next
          </Button>
        )}
        {activeStep === steps.length - 1 && (
          <LoadingButton
            variant="outlined"
            color="primary"
            onClick={handleSaveForm}
          >
            Submit
          </LoadingButton>
        )}
      </Box>
    </Container>
  );
};

export default RegistrationForm;
